import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LensePanel, PageLoadOverlay, useMobileSidePanel } from '@hofsync/design-system';
import { useMatchGroups, matchGroupKeys } from '../../../hooks/useMatchGroups';
import { twinService } from '../../../services/twinService';
import { usePageLoadTelemetry } from '../../../hooks/usePageLoadTelemetry';
import { useToast } from '../../../contexts/ToastContext';
import { applySuppression, matchesCanvasStatusFilter } from './resolvedExclusion';
import FilterBar from './FilterBar';
import MatchGraph from './MatchGraph';
import DocumentPreviewPanel from './DocumentPreviewPanel';
import { isSeasonMode, monthsToLenseItems } from './monthLensItems';
import './GutschriftCheckPage.css';

function currentYM(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function GutschriftCheckPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  // #1393 codex r2 P2 — the editor is month-scoped and does NOT support the
  // Gesamt season sentinel (a feed-only view). If it leaks in (the feed's
  // Editor link, or a direct ``?ym=gesamt``), treat it as "no explicit month"
  // so the editor falls back to the current month + its existing auto-advance
  // to the latest non-empty month, and NEVER passes the sentinel into the
  // shared ``useMatchGroups`` hook (which would hydrate every month at once).
  const rawYm = searchParams.get('ym');
  const hasExplicitMonth = rawYm != null && !isSeasonMode(rawYm);
  const tenantMonth = hasExplicitMonth ? rawYm : currentYM();

  const [statusFilter, setStatusFilter] = useState('');
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  // Optimistic override applied on top of each orphan event's
  // persisted ``manually_resolved_at`` attribute. Keyed by entity id
  // (settlement_event or delivery_event). ``true`` = optimistically
  // resolved while the editEntity write is in flight; ``false`` =
  // optimistically reverted; absent = trust persisted state. Survives
  // a refetch so the tile doesn't flicker between the mutation
  // success and the worker draining its coalescing buffer.
  const [resolvedOverrides, setResolvedOverrides] = useState<Map<string, boolean>>(
    () => new Map(),
  );
  // #1395 — optimistic suppression override, keyed by the event entity
  // id. ``true`` = optimistically suppressed while the editEntity write
  // is in flight; ``false`` = optimistically un-suppressed; absent =
  // trust the persisted ``suppressed_at`` attribute. Survives a refetch
  // so a suppressed tile doesn't flicker back between the mutation
  // success and the worker re-materialising the match-group facts.
  const [suppressOverrides, setSuppressOverrides] = useState<Map<string, boolean>>(
    () => new Map(),
  );
  // #1396 — optimistic flag override, keyed by the event entity id.
  // ``true`` = optimistically flagged while the editEntity write is in
  // flight; ``false`` = optimistically un-flagged; absent = trust the
  // persisted ``flagged_at`` attribute. The companion comment-override map
  // carries the just-typed note so the tile's flag tooltip (and the feed
  // sub-line) reflect it before the worker re-materialises the facts. The
  // flag is NON-destructive — it never removes a doc from any view — so
  // there is no ``applyFlag`` exclusion step; these maps only drive the
  // marker + note rendering.
  const [flagOverrides, setFlagOverrides] = useState<Map<string, boolean>>(() => new Map());
  const [flagCommentOverrides, setFlagCommentOverrides] = useState<Map<string, string | null>>(
    () => new Map(),
  );

  const { showToast } = useToast();

  const { groups, availableMonths, gsCountsByMonth, gsCountsByYear, isLoading, error } =
    useMatchGroups(tenantMonth);

  // #916 — platform-wide page-load overlay (same primitive ``/farm`` and
  // ``/documents`` use). Replaces the inline ``Loader2`` spinner; buys
  // the 1-second flicker threshold, the elapsed-seconds counter, and the
  // single-POST-per-slow-load telemetry path under the
  // ``page: 'gutschrift-check'`` ELK bucket (#643). View descriptor
  // mirrors ``/farm``'s shape: ``gutschrift-check:ym=<YYYY-MM>[:status=…]``.
  const pageLoadOverlay = usePageLoadTelemetry({
    page: 'gutschrift-check',
    view: `gutschrift-check:ym=${tenantMonth}${statusFilter ? `:status=${statusFilter}` : ''}`,
    isLoading,
  });

  const queryClient = useQueryClient();

  // Mark / unmark are symmetric writes against the entity's
  // ``manually_resolved_at`` attribute via the standard editEntity
  // path. No custom endpoint — the digital twin IS the storage. The
  // optimistic override flips immediately in onMutate so the tile
  // doesn't wait for the round-trip; onError rolls back; onSuccess
  // invalidates matchGroup queries so a refetch picks up the
  // persisted timestamp once the worker drains.
  const markResolvedMutation = useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      twinService.editEntity(entityType, entityId, {
        manually_resolved_at: new Date().toISOString(),
      }),
    onMutate: ({ entityId }) => {
      setResolvedOverrides((prev) => {
        const next = new Map(prev);
        next.set(entityId, true);
        return next;
      });
    },
    onError: (_err, { entityId }) => {
      setResolvedOverrides((prev) => {
        const next = new Map(prev);
        next.delete(entityId);
        return next;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: matchGroupKeys.all });
    },
  });

  const unmarkResolvedMutation = useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      twinService.editEntity(entityType, entityId, { manually_resolved_at: null }),
    onMutate: ({ entityId }) => {
      setResolvedOverrides((prev) => {
        const next = new Map(prev);
        next.set(entityId, false);
        return next;
      });
    },
    onError: (_err, { entityId }) => {
      setResolvedOverrides((prev) => {
        const next = new Map(prev);
        next.delete(entityId);
        return next;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: matchGroupKeys.all });
    },
  });

  const handleMarkResolved = useCallback(
    (entityType: 'settlement_event' | 'delivery_event', entityId: string) => {
      markResolvedMutation.mutate({ entityType, entityId });
    },
    [markResolvedMutation],
  );

  const handleUnmarkResolved = useCallback(
    (entityType: 'settlement_event' | 'delivery_event', entityId: string) => {
      unmarkResolvedMutation.mutate({ entityType, entityId });
    },
    [unmarkResolvedMutation],
  );

  // #1395 — suppress / un-suppress an erroneous document (QK /
  // Papierbestellung etc.). Symmetric editEntity writes against the
  // event's ``suppressed_at`` attribute — same "the twin IS the
  // storage" pattern as resolution, no bespoke endpoint. Suppression is
  // STRONGER than resolution: ``applySuppression`` below removes the
  // event from the canvas, the feed AND the dashboard totals, so the
  // optimistic override makes the tile vanish on click instead of
  // greying it. ``onSuccess`` toasts the ops-notification confirmation
  // (the persisted ``suppressed_at`` + backend-stamped actor is the
  // audit trail) and invalidates the match-group queries so the
  // re-materialised facts land.
  const suppressMutation = useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      twinService.editEntity(entityType, entityId, {
        suppressed_at: new Date().toISOString(),
      }),
    onMutate: ({ entityId }) => {
      setSuppressOverrides((prev) => {
        const next = new Map(prev);
        next.set(entityId, true);
        return next;
      });
    },
    onError: (_err, { entityId }) => {
      setSuppressOverrides((prev) => {
        const next = new Map(prev);
        next.delete(entityId);
        return next;
      });
      showToast('Beleg konnte nicht ausgeblendet werden.', 'error');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: matchGroupKeys.all });
      showToast('Beleg ausgeblendet — das Team wurde benachrichtigt.', 'success');
    },
  });

  const unsuppressMutation = useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      twinService.editEntity(entityType, entityId, { suppressed_at: null }),
    onMutate: ({ entityId }) => {
      setSuppressOverrides((prev) => {
        const next = new Map(prev);
        next.set(entityId, false);
        return next;
      });
    },
    onError: (_err, { entityId }) => {
      setSuppressOverrides((prev) => {
        const next = new Map(prev);
        next.delete(entityId);
        return next;
      });
      showToast('Beleg konnte nicht wieder eingeblendet werden.', 'error');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: matchGroupKeys.all });
      showToast('Beleg wieder eingeblendet.', 'success');
    },
  });

  const handleSuppress = useCallback(
    (entityType: 'settlement_event' | 'delivery_event', entityId: string) => {
      suppressMutation.mutate({ entityType, entityId });
    },
    [suppressMutation],
  );

  const handleUnsuppress = useCallback(
    (entityType: 'settlement_event' | 'delivery_event', entityId: string) => {
      unsuppressMutation.mutate({ entityType, entityId });
    },
    [unsuppressMutation],
  );

  // #1396 — flag + comment a document. Like resolution / suppression this
  // is a plain ``editEntity`` write against the event's own
  // ``flagged_at`` + ``flag_comment`` attributes — the twin IS the
  // storage, no bespoke endpoint. UNLIKE suppression the flag is
  // NON-destructive: the document stays in every view + the totals; the
  // flag only surfaces a marker (editor) and a user-visible note (feed)
  // capturing *why* the reviewer singled it out. The optimistic override
  // flips the marker + carries the note instantly; onError rolls both
  // back; onSuccess re-fetches so the persisted attributes land.
  const flagMutation = useMutation({
    mutationFn: ({
      entityType,
      entityId,
      comment,
    }: {
      entityType: string;
      entityId: string;
      comment: string;
    }) =>
      twinService.editEntity(entityType, entityId, {
        flagged_at: new Date().toISOString(),
        // Persist the trimmed note, or NULL for a bare flag, so the worker
        // stores a clean value (the prompt may return surrounding spaces).
        flag_comment: comment.trim().length > 0 ? comment.trim() : null,
      }),
    onMutate: ({ entityId, comment }) => {
      const trimmed = comment.trim();
      setFlagOverrides((prev) => {
        const next = new Map(prev);
        next.set(entityId, true);
        return next;
      });
      setFlagCommentOverrides((prev) => {
        const next = new Map(prev);
        next.set(entityId, trimmed.length > 0 ? trimmed : null);
        return next;
      });
    },
    onError: (_err, { entityId }) => {
      setFlagOverrides((prev) => {
        const next = new Map(prev);
        next.delete(entityId);
        return next;
      });
      setFlagCommentOverrides((prev) => {
        const next = new Map(prev);
        next.delete(entityId);
        return next;
      });
      showToast('Markierung konnte nicht gespeichert werden.', 'error');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: matchGroupKeys.all });
      showToast('Beleg markiert.', 'success');
    },
  });

  const unflagMutation = useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      twinService.editEntity(entityType, entityId, { flagged_at: null, flag_comment: null }),
    onMutate: ({ entityId }) => {
      setFlagOverrides((prev) => {
        const next = new Map(prev);
        next.set(entityId, false);
        return next;
      });
      setFlagCommentOverrides((prev) => {
        const next = new Map(prev);
        next.set(entityId, null);
        return next;
      });
    },
    onError: (_err, { entityId }) => {
      setFlagOverrides((prev) => {
        const next = new Map(prev);
        next.delete(entityId);
        return next;
      });
      setFlagCommentOverrides((prev) => {
        const next = new Map(prev);
        next.delete(entityId);
        return next;
      });
      showToast('Markierung konnte nicht entfernt werden.', 'error');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: matchGroupKeys.all });
      showToast('Markierung entfernt.', 'success');
    },
  });

  // The kebab hands the existing comment so a re-flag ("Markierung
  // bearbeiten") pre-fills the prompt. ``window.prompt`` returns ``null``
  // on Cancel — we abort the write entirely so a mis-click leaves the
  // current flag state untouched (an empty string, by contrast, is a
  // deliberate bare flag / cleared note and DOES write).
  const handleFlag = useCallback(
    (
      entityType: 'settlement_event' | 'delivery_event',
      entityId: string,
      existingComment: string,
    ) => {
      const comment = window.prompt(
        'Kommentar zur Markierung (für den Nutzer sichtbar):',
        existingComment,
      );
      if (comment === null) return;
      flagMutation.mutate({ entityType, entityId, comment });
    },
    [flagMutation],
  );

  const handleUnflag = useCallback(
    (entityType: 'settlement_event' | 'delivery_event', entityId: string) => {
      unflagMutation.mutate({ entityType, entityId });
    },
    [unflagMutation],
  );

  // #1051 — manual line-link mutation. The backend endpoint is
  // synchronous (UDB upsert in the request thread), so we DON'T need
  // a Redis-coalescing optimistic state on the resolved-override map;
  // optimistic edges are managed inside MatchGraph (where the drag
  // surface lives) and cleared once the mutation settles.
  const manualLinkMutation = useMutation({
    mutationFn: (args: {
      delivery_line_id: string;
      settlement_line_id: string;
      action: 'link' | 'unlink';
      match_status?: 'matched' | 'quantity_mismatch';
    }) => twinService.linkEventLines(args),
    // Codex round-2 P2: return the invalidation promise so
    // ``mutateAsync`` doesn't resolve until the match-groups refetch
    // is in flight. ``MatchGraph`` clears the optimistic edge in
    // ``.finally``; awaiting here keeps the optimistic edge on screen
    // until the real edge from the refetched groups arrives, avoiding
    // a one-frame flicker on successful link/unlink.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: matchGroupKeys.all }),
  });

  const handleManualLink = useCallback(
    (args: { delivery_line_id: string; settlement_line_id: string }) =>
      manualLinkMutation.mutateAsync({ ...args, action: 'link', match_status: 'matched' }),
    [manualLinkMutation],
  );

  const handleManualUnlink = useCallback(
    (args: { delivery_line_id: string; settlement_line_id: string }) =>
      manualLinkMutation.mutateAsync({ ...args, action: 'unlink' }),
    [manualLinkMutation],
  );

  // HOF-219 — the DOCUMENT-level unlink. Its own mutation rather than a mode on
  // ``manualLinkMutation``: different endpoint, different payload shape, and a
  // shared ``isPending`` would make a line unlink appear to block a document
  // one. The invalidation is the same and is returned for the same reason —
  // ``mutateAsync`` must not resolve until the match-groups refetch is in
  // flight, because the canvas deliberately keeps the severed edge on screen
  // until that refetch takes it away.
  const documentLinkMutation = useMutation({
    mutationFn: (args: {
      settlement_event_id: string;
      delivery_event_id: string;
      action: 'link' | 'unlink';
    }) => twinService.linkDocuments(args),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: matchGroupKeys.all }),
  });

  const handleDocumentUnlink = useCallback(
    (args: { settlement_event_id: string; delivery_event_id: string }) =>
      documentLinkMutation.mutateAsync({ ...args, action: 'unlink' }),
    [documentLinkMutation],
  );

  const handleMonthChange = useCallback(
    (ym: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('ym', ym);
        return next;
      });
      setSelectedDocId(null);
    },
    [setSearchParams],
  );

  // #1287 — lens-panel (year → month) selection wiring. ``?ym=`` stays
  // the single source of truth: a MONTH sub-item click routes through
  // the same ``handleMonthChange`` as everything else, while a bare
  // YEAR click only expands/collapses — a deviation from /documents
  // and /farm, where a parent click navigates. The LensePanel has no
  // local expand toggle (expansion is driven solely by its
  // ``selectedItem`` prop, and ``''`` collapses everything via the
  // panel's effect), so the page keeps a derived "expanded year":
  // ``yearOverride`` only exists between a bare year click and the
  // next month change, otherwise the active month's year is expanded.
  const [yearOverride, setYearOverride] = useState<string | null>(null);
  const expandedYear = yearOverride ?? tenantMonth.slice(0, 4);

  // Re-track the active month's year whenever ``?ym=`` changes from
  // anywhere — month click, auto-advance, deep link, back/forward.
  useEffect(() => {
    setYearOverride(null);
  }, [tenantMonth]);

  // Mobile: MainLayout's hamburger drives the drawer (same one-liner
  // as DocumentsPageLayout); outside MainLayout the hook returns null.
  const mobilePanel = useMobileSidePanel();

  // Codex round-1 P2: LensePanel auto-closes the mobile drawer after
  // EVERY selection, but a bare year click only expands/collapses —
  // closing there would force the user to reopen the drawer just to
  // pick a month from the newly expanded year. The panel invokes
  // ``onSelect`` synchronously before ``onMobileClose``, so a one-shot
  // ref set in the bare-year branch suppresses exactly the close that
  // follows it. Overlay / close-button closes don't pass through
  // ``onSelect`` and are never suppressed.
  const suppressNextMobileCloseRef = useRef(false);

  const handleLensSelect = useCallback(
    (itemId: string, subItemId: string | null) => {
      if (subItemId) {
        handleMonthChange(subItemId);
        setYearOverride(null);
        return;
      }
      // Bare year click: toggle. '' collapses (the panel maps falsy →
      // collapsed); clicking a collapsed/other year expands it.
      if (mobilePanel?.isOpen) {
        suppressNextMobileCloseRef.current = true;
      }
      setYearOverride(itemId === expandedYear ? '' : itemId);
    },
    [handleMonthChange, expandedYear, mobilePanel],
  );

  const handleMobileClose = useCallback(() => {
    if (suppressNextMobileCloseRef.current) {
      suppressNextMobileCloseRef.current = false;
      return;
    }
    mobilePanel?.close();
  }, [mobilePanel]);

  const lensItems = useMemo(
    () => monthsToLenseItems(availableMonths, gsCountsByMonth, gsCountsByYear),
    [availableMonths, gsCountsByMonth, gsCountsByYear],
  );

  // If the page was opened without an explicit ``?ym=`` param AND the
  // implicit "current month" is empty, auto-advance to the most recent
  // month with data so the operator lands somewhere productive. Once
  // the user has expressed a month choice (via this redirect, the
  // chevron buttons, or a deep link), we MUST stop auto-advancing —
  // otherwise a deliberate click into an empty April bounces straight
  // back to May and the month switcher reads as broken. The
  // ``hasExplicitMonth`` (defined above — a real ``?ym=YYYY-MM``, NOT the
  // Gesamt sentinel) gates the bounce strictly to the initial load path.
  // ``availableMonths`` comes from the hook's unfiltered entity set (codex
  // P2 fix).
  useEffect(() => {
    if (hasExplicitMonth) return;
    if (isLoading || groups.length > 0) return;
    if (availableMonths.length === 0) return;
    const latestYM = availableMonths[availableMonths.length - 1];
    if (latestYM && latestYM !== tenantMonth) {
      handleMonthChange(latestYM);
    }
  }, [hasExplicitMonth, isLoading, groups, tenantMonth, handleMonthChange, availableMonths]);

  // #1395 — suppression mirrors resolution: a suppressed erroneous doc
  // (QK / Papierbestellung) drops from the dashboard totals AND the
  // farmer-facing feed, but stays VISIBLE on the data-administration
  // canvas — muted, with an "Wieder einblenden" kebab action — so ops
  // can see what was suppressed and reverse it. The FILTERBAR therefore
  // gets the suppression-stripped set (``dashboardGroups``) while the
  // CANVAS keeps the full ``groups`` so the suppressed tile still
  // renders its revert affordance. ``applySuppression`` honours the
  // optimistic override so the totals react the instant the kebab fires.
  const dashboardGroups = useMemo(
    () => applySuppression(groups, suppressOverrides),
    [groups, suppressOverrides],
  );

  // Operator-resolved orphans stay on the canvas — their tile renders
  // grey-spotted with the "Erledigung rückgängig machen" kebab action.
  // Suppressed tiles likewise stay (muted) with their revert action.
  // The dashboard totals exclude both (resolved via FilterBar's
  // resolvedOverrides; suppressed via the ``dashboardGroups`` set).
  //
  // #1436 — filter the canvas on the suppression-ADJUSTED status (the
  // same status ``FilterBar`` counts via ``dashboardGroups``) so the tile
  // count matches the rows the filter reveals: suppressing one side of a
  // ``partial`` group turns it into ``orphan_delivery``; the
  // "Gutschrift fehlt" tile must reveal it, not hide it on the stale raw
  // ``g.status``. We filter the RAW ``groups`` (not ``dashboardGroups``)
  // and hand the RAW group to ``MatchGraph`` so the suppressed tile keeps
  // rendering muted with its "Wieder einblenden" affordance — a fully-
  // suppressed group stays visible under any filter so it can be reverted.
  const filteredGroups = useMemo(
    () => groups.filter((g) => matchesCanvasStatusFilter(g, statusFilter, suppressOverrides)),
    [groups, statusFilter, suppressOverrides],
  );

  return (
    <div className="gutschrift-check-page">
      {/* #1287 — year → month lens sidebar (same organism as /documents
          and /farm). position:fixed on desktop, so DOM placement is
          cosmetic; the page CSS reserves its 12rem width. */}
      <LensePanel
        title={t('appPanels.gutschriftCheck', 'GS-Check')}
        items={lensItems}
        selectedItem={expandedYear}
        selectedSubItem={tenantMonth}
        onSelect={handleLensSelect}
        isMobileOpen={mobilePanel?.isOpen ?? false}
        onMobileClose={handleMobileClose}
      />
      {/* #916 — portal-rendered platform-wide overlay; physical placement
          here is cosmetic but mirrors FarmPage.tsx and DocumentsPage.tsx
          for git-blame predictability. */}
      <PageLoadOverlay
        visible={pageLoadOverlay.showOverlay}
        elapsedSeconds={pageLoadOverlay.elapsedSeconds}
      />
      {/* #1394 — symmetric editor-exit link in the tiles/header panel,
          mirroring the feed's "Editor" link position. ``?ym=`` carries
          back so the overview opens on the same month. */}
      <FilterBar
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        groups={dashboardGroups}
        resolvedOverrides={resolvedOverrides}
        navLink={{ to: `/apps/gutschrift-check?ym=${tenantMonth}`, label: '← Übersicht' }}
      />

      <div className="gutschrift-check-page__workspace">
        <div className="gutschrift-check-page__canvas">
          {/* #916 — inline ``Loader2`` spinner removed in favour of the
              platform-wide ``<PageLoadOverlay>`` mounted above. */}
          {!isLoading && filteredGroups.length === 0 && (
            <div className="gutschrift-check-page__empty">
              Keine Lieferscheine oder Gutschriften für {(() => {
                const [y, m] = tenantMonth.split('-').map(Number);
                return new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
              })()}.
            </div>
          )}
          {!isLoading && error && filteredGroups.length > 0 && (
            <div className="gutschrift-check-page__error" role="status">
              Hinweis: Einige Daten konnten nicht geladen werden.
            </div>
          )}
          {!isLoading && filteredGroups.length > 0 && (
            <MatchGraph
              groups={filteredGroups}
              // Pass the unfiltered month set so the per-tile orphan-
              // delivery price estimate stays anchored to the same
              // paired evidence the dashboard uses, even when the
              // operator filters the canvas down to a single status.
              // #1395 codex P2 — use the suppression-stripped
              // ``dashboardGroups`` (NOT raw ``groups``) so a suppressed
              // Gutschrift's settlement lines stop seeding the price index;
              // ``buildPriceIndex(allGroups)`` would otherwise keep
              // distorting missing-GS estimates after the doc was hidden,
              // diverging from the feed path which already uses the
              // stripped set.
              allGroups={dashboardGroups}
              selectedDocId={selectedDocId}
              onNodeClick={({ documentId }) => {
                if (!documentId) return;
                setSelectedDocId((prev) => (prev === documentId ? null : documentId));
              }}
              onPaneClick={() => setSelectedDocId(null)}
              resolvedOverrides={resolvedOverrides}
              onMarkResolved={handleMarkResolved}
              onUnmarkResolved={handleUnmarkResolved}
              suppressOverrides={suppressOverrides}
              onSuppress={handleSuppress}
              onUnsuppress={handleUnsuppress}
              flagOverrides={flagOverrides}
              flagCommentOverrides={flagCommentOverrides}
              onFlag={handleFlag}
              onUnflag={handleUnflag}
              onManualLink={handleManualLink}
              onManualUnlink={handleManualUnlink}
              onDocumentUnlink={handleDocumentUnlink}
            />
          )}
        </div>
        {/* #835 + #887 — preview panel auto-hides when no document is
            selected. The component returns ``null`` internally when
            ``documentId === null`` (#835 contract), so the canvas
            (``flex: 1 1 auto``) STILL reclaims full viewport width on
            deselect: there is no <aside> in the DOM to occupy a flex
            slot. Keeping the panel mounted (rather than gating with
            ``selectedDocId && …``) hands the null transition to the
            component's existing ``[documentId]`` effect, which revokes
            the previously loaded ``URL.createObjectURL`` blob via a
            functional ``setBlobUrl`` updater. Pre-#887 the page-level
            conditional unmounted the panel before that revoke could
            run, and the unmount-only cleanup captured ``blobUrl`` from
            the first render's closure (``null``) — every deselect
            leaked one Blob until reload. Closes #887. */}
        {/* #924 — onClose drives the floating Close + Edit icon buttons.
            Wires to the same ``setSelectedDocId(null)`` callback as the
            canvas's ``onPaneClick`` handler above so both code paths
            converge on the single source of truth for "no doc
            selected". */}
        <DocumentPreviewPanel
          documentId={selectedDocId}
          onClose={() => setSelectedDocId(null)}
        />
      </div>
    </div>
  );
}
